import { CheckCircle, Info, WarningCircle } from "@phosphor-icons/react";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";
import { WarningAlertIcon, warningSurfaceTextStyle } from "../warningVisual";

const alertVariants = cva("bfrs-flex bfrs-rounded-xl bfrs-border", {
  variants: {
    tone: {
      info: "bfrs-border-info/20 bfrs-bg-info-soft bfrs-text-info-contrast",
      success: "bfrs-border-success/20 bfrs-bg-success-soft bfrs-text-success-contrast",
      warning: "",
      danger: "bfrs-border-danger/20 bfrs-bg-danger-soft bfrs-text-danger"
    },
    size: {
      xs: "bfrs-gap-1 bfrs-p-1.5 bfrs-text-[10px]",
      sm: "bfrs-gap-2 bfrs-p-2.5 bfrs-text-xs",
      md: "bfrs-gap-3 bfrs-p-4 bfrs-text-sm",
      lg: "bfrs-gap-4 bfrs-p-5 bfrs-text-base"
    }
  },
  defaultVariants: {
    tone: "info",
    size: "md"
  }
});

const iconSizeMap = { xs: 10, sm: 14, md: 18, lg: 20 } as const;

const iconMap = {
  info: Info,
  success: CheckCircle,
  danger: WarningCircle
};

export type AlertProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> &
  VariantProps<typeof alertVariants> & {
    title?: ReactNode;
    message?: ReactNode;
    description?: ReactNode;
    action?: ReactNode;
    icon?: ReactNode;
  };

function hasRenderableNode(node: ReactNode): boolean {
  if (Array.isArray(node)) return node.some(hasRenderableNode);
  return node !== undefined && node !== null && node !== false && node !== "";
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(({ className, tone, size = "md", title, message, description, action, icon, children, style, ...props }, ref) => {
  const resolvedTone = tone ?? "info";
  const Icon = resolvedTone === "warning" ? WarningAlertIcon : iconMap[resolvedTone];
  const warningStyle = resolvedTone === "warning" ? warningSurfaceTextStyle : undefined;
  const body = hasRenderableNode(children) ? children : (message ?? description);
  const hasTitle = hasRenderableNode(title);
  const hasBody = hasRenderableNode(body);

  return (
    <div ref={ref} role={resolvedTone === "danger" ? "alert" : "status"} className={cn(alertVariants({ tone: resolvedTone, size }), "bfrs-items-start", className)} style={{ ...warningStyle, ...style }} {...props}>
      <span className="bfrs-mt-[1px] bfrs-flex bfrs-shrink-0 bfrs-items-center">
        {icon ?? (resolvedTone === "warning" ? <Icon /> : <Icon size={iconSizeMap[size ?? "md"]} weight="bold" />)}
      </span>
      <div className="bfrs-min-w-0 bfrs-flex-1">
        {hasTitle && <div className={cn("bfrs-font-semibold bfrs-leading-5 bfrs-text-text-primary", hasBody && "bfrs-mb-1")}>{title}</div>}
        {hasBody && <div className="bfrs-leading-5">{body}</div>}
      </div>
      {action && <div className="bfrs-shrink-0">{action}</div>}
    </div>
  );
});

Alert.displayName = "Alert";
